export interface RelatedObject {
  id: string;
  label: string;
  kind: string;
  description?: string;
}
export interface HierarchyNode extends RelatedObject {
  parentId: string | null;
}
export interface TreeRow {
  node: HierarchyNode;
  level: number;
  position: number;
  siblings: number;
  hasChildren: boolean;
}
export type DictionaryTone = 'neutral' | 'info' | 'success' | 'warn';
export interface DictionaryEntry {
  id: string;
  label: string;
  tone: DictionaryTone;
  archived?: boolean;
}
export interface AccessRole {
  id: string;
  label: string;
  description?: string;
}
export interface AccessAction {
  id: string;
  label: string;
}
export type AccessGrants = Readonly<Record<string, readonly string[]>>;
export type RelationState = 'ready' | 'loading' | 'error';

export function validateRelated(objects: readonly RelatedObject[]): string | null {
  const seen = new Set<string>();
  for (const item of objects) {
    if (!item.id.trim() || !item.label.trim()) return 'У объекта нет идентификатора или названия.';
    if (seen.has(item.id)) return 'Идентификатор объекта повторяется.';
    seen.add(item.id);
  }
  return null;
}
export function availableObjects(all: readonly RelatedObject[], linkedIds: readonly string[], query = '') {
  const linked = new Set(linkedIds);
  const words = query.normalize('NFKC').toLocaleLowerCase('ru').trim().split(/\s+/).filter(Boolean);
  return all.filter(
    (item) =>
      !linked.has(item.id) &&
      words.every((word) =>
        (item.label + ' ' + item.kind + ' ' + (item.description ?? ''))
          .normalize('NFKC')
          .toLocaleLowerCase('ru')
          .includes(word),
      ),
  );
}
export function hierarchyError(nodes: readonly HierarchyNode[]): string | null {
  const invalid = validateRelated(nodes);
  if (invalid) return invalid;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  for (const node of nodes) {
    if (node.parentId !== null && !byId.has(node.parentId)) return 'Родитель объекта не найден.';
    const parents = new Set<string>();
    let cursor: HierarchyNode | undefined = node;
    while (cursor) {
      if (parents.has(cursor.id)) return 'Вложенность содержит цикл.';
      parents.add(cursor.id);
      cursor = cursor.parentId === null ? undefined : byId.get(cursor.parentId);
    }
  }
  return null;
}
export function visibleHierarchy(nodes: readonly HierarchyNode[], expanded: readonly string[]): TreeRow[] {
  if (hierarchyError(nodes)) return [];
  const open = new Set(expanded),
    children = new Map<string | null, HierarchyNode[]>();
  for (const node of nodes) children.set(node.parentId, [...(children.get(node.parentId) ?? []), node]);
  const rows: TreeRow[] = [];
  function visit(parent: string | null, level: number) {
    const list = children.get(parent) ?? [];
    list.forEach((node, index) => {
      const hasChildren = !!children.get(node.id)?.length;
      rows.push({
        node,
        level,
        position: index + 1,
        siblings: list.length,
        hasChildren,
      });
      if (hasChildren && open.has(node.id)) visit(node.id, level + 1);
    });
  }
  visit(null, 1);
  return rows;
}
export function validateDictionary(entries: readonly DictionaryEntry[]): string | null {
  const seenIds = new Set<string>(),
    names = new Set<string>();
  for (const entry of entries) {
    if (!entry.id.trim()) return 'У значения нет постоянного идентификатора.';
    if (seenIds.has(entry.id)) return 'Идентификатор значения повторяется.';
    seenIds.add(entry.id);
    const name = entry.label.normalize('NFKC').trim().toLocaleLowerCase('ru');
    if (!name) return 'Введите название.';
    if (entry.label.trim().length > 80) return 'Название — не больше 80 символов.';
    if (!['neutral', 'info', 'success', 'warn'].includes(entry.tone)) return 'Неизвестный смысловой цвет.';
    if (!entry.archived && names.has(name)) return 'Активное значение с таким названием уже есть.';
    if (!entry.archived) names.add(name);
  }
  return null;
}
export function moveDictionary(
  entries: readonly DictionaryEntry[],
  id: string,
  direction: -1 | 1,
): DictionaryEntry[] {
  const next = [...entries],
    from = next.findIndex((entry) => entry.id === id),
    to = from + direction;
  if (from < 0 || to < 0 || to >= next.length) return next;
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}
export function accessError(
  roles: readonly AccessRole[],
  actions: readonly AccessAction[],
  grants: AccessGrants,
): string | null {
  const roleIds = new Set(roles.map((role) => role.id)),
    actionIds = new Set(actions.map((action) => action.id));
  if (
    roleIds.size !== roles.length ||
    actionIds.size !== actions.length ||
    roles.some((role) => !role.id.trim() || !role.label.trim()) ||
    actions.some((action) => !action.id.trim() || !action.label.trim())
  )
    return 'Роли и действия должны иметь уникальные идентификаторы и названия.';
  for (const [roleId, permissions] of Object.entries(grants)) {
    if (
      !roleIds.has(roleId) ||
      new Set(permissions).size !== permissions.length ||
      permissions.some((permission) => !actionIds.has(permission))
    )
      return 'Права ссылаются на неизвестную роль или действие.';
  }
  return null;
}
export function changeGrant(
  grants: AccessGrants,
  roleId: string,
  actionId: string,
  enabled: boolean,
): AccessGrants {
  const current = grants[roleId] ?? [];
  return {
    ...grants,
    [roleId]: enabled ? [...new Set([...current, actionId])] : current.filter((id) => id !== actionId),
  };
}
export function visibleTo(roles: readonly AccessRole[], grants: AccessGrants, viewAction = 'view') {
  return roles.filter((role) => grants[role.id]?.includes(viewAction));
}
